import type { ReactElement, SVGProps } from 'react';
import type { ItemType, Visibility } from '../api/types';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Svg({ size = 20, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconOrbit = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="3.4" />
    <ellipse cx="12" cy="12" rx="10" ry="4.6" transform="rotate(-24 12 12)" />
  </Svg>
);
export const IconLibrary = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 5.5h4.5V19H4z" />
    <path d="M9.5 5.5H14V19H9.5z" />
    <path d="m15.6 6 3.6.9-2.9 11.5-3.6-.9" />
  </Svg>
);
export const IconLayers = (p: IconProps) => (
  <Svg {...p}>
    <path d="m12 3.6 8 4.2-8 4.2-8-4.2z" />
    <path d="m4 12.6 8 4.2 8-4.2" />
    <path d="m4 16.8 8 4.2 8-4.2" />
  </Svg>
);
export const IconPulse = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 12h3.6l2-5 3 10 2.4-7 1.6 2H21" />
  </Svg>
);
export const IconUser = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="8.2" r="3.6" />
    <path d="M5 19.4c1.3-3.2 4-4.8 7-4.8s5.7 1.6 7 4.8" />
  </Svg>
);
export const IconUsers = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9.4" cy="8.4" r="3.3" />
    <path d="M3.4 19c1-2.9 3.3-4.4 6-4.4s5 1.5 6 4.4" />
    <path d="M16.4 5.4a3.2 3.2 0 0 1 0 6.1M18 14.8c1.4.7 2.3 2 2.7 3.7" />
  </Svg>
);
export const IconSearch = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10.8" cy="10.8" r="6.2" />
    <path d="m15.6 15.6 4 4" />
  </Svg>
);
export const IconPlus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);
export const IconClose = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Svg>
);
export const IconCheck = (p: IconProps) => (
  <Svg {...p}>
    <path d="m5 12.8 4.4 4.4L19 7.4" />
  </Svg>
);
export const IconGrip = (p: IconProps) => (
  <Svg {...p} strokeWidth={1.4}>
    <circle cx="9.5" cy="6.5" r="1.1" fill="currentColor" />
    <circle cx="14.5" cy="6.5" r="1.1" fill="currentColor" />
    <circle cx="9.5" cy="12" r="1.1" fill="currentColor" />
    <circle cx="14.5" cy="12" r="1.1" fill="currentColor" />
    <circle cx="9.5" cy="17.5" r="1.1" fill="currentColor" />
    <circle cx="14.5" cy="17.5" r="1.1" fill="currentColor" />
  </Svg>
);
export const IconArrowUp = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 19V5.6M6.6 11 12 5.6 17.4 11" />
  </Svg>
);
export const IconArrowDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v13.4M6.6 13 12 18.4 17.4 13" />
  </Svg>
);
export const IconBack = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14.5 5.5 8 12l6.5 6.5" />
  </Svg>
);
export const IconTrash = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.8 7h14.4M9.5 7V4.8h5V7M6.6 7l.9 12.2h9l.9-12.2M10.4 10.4v5.8M13.6 10.4v5.8" />
  </Svg>
);
export const IconPencil = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.5 19.5h3.2L19 8.2a1.9 1.9 0 0 0-2.7-2.7L5 16.8z" />
    <path d="m14.8 6.9 2.4 2.4" />
  </Svg>
);
export const IconLink = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10.2 13.8a3.6 3.6 0 0 0 5.1 0l2.6-2.6a3.6 3.6 0 0 0-5.1-5.1l-1.1 1.1" />
    <path d="M13.8 10.2a3.6 3.6 0 0 0-5.1 0L6.1 12.8a3.6 3.6 0 0 0 5.1 5.1l1.1-1.1" />
  </Svg>
);
export const IconExternal = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 5h5v5M19 5l-7.5 7.5" />
    <path d="M18 14v4.4a1.6 1.6 0 0 1-1.6 1.6H6.6A1.6 1.6 0 0 1 5 18.4V9.6A1.6 1.6 0 0 1 6.6 8H11" />
  </Svg>
);
export const IconEye = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2.8 12S6.4 6.2 12 6.2 21.2 12 21.2 12S17.6 17.8 12 17.8 2.8 12 2.8 12" />
    <circle cx="12" cy="12" r="2.7" />
  </Svg>
);
export const IconLock = (p: IconProps) => (
  <Svg {...p}>
    <rect x="5.2" y="10.4" width="13.6" height="9.4" rx="2.2" />
    <path d="M8.6 10.4V8a3.4 3.4 0 0 1 6.8 0v2.4" />
  </Svg>
);
export const IconGlobe = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.4" />
    <path d="M3.8 9.6h16.4M3.8 14.4h16.4M12 3.6c2.6 3 2.6 13.8 0 16.8-2.6-3-2.6-13.8 0-16.8" />
  </Svg>
);
export const IconLogout = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14.5 8V5.6a1.6 1.6 0 0 0-1.6-1.6H6.6A1.6 1.6 0 0 0 5 5.6v12.8a1.6 1.6 0 0 0 1.6 1.6h6.3a1.6 1.6 0 0 0 1.6-1.6V16" />
    <path d="M10 12h9.4M16.4 8.8 19.6 12l-3.2 3.2" />
  </Svg>
);
export const IconSettings = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5.4 8.4h6.2M15.4 8.4h3.2M5.4 15.6h3.2M12.4 15.6h6.2" />
    <circle cx="13.6" cy="8.4" r="2" />
    <circle cx="10.6" cy="15.6" r="2" />
  </Svg>
);
export const IconAlert = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4.6 21 19.4H3z" />
    <path d="M12 9.8v4.4M12 16.6v.6" />
  </Svg>
);
export const IconInfo = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.4" />
    <path d="M12 11v5.4M12 7.8v.5" />
  </Svg>
);
export const IconText = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 7h14M5 11.5h10M5 16h12" />
  </Svg>
);
export const IconCard = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.6" y="5.6" width="16.8" height="12.8" rx="2.2" />
    <path d="M7.4 14.6h6" />
  </Svg>
);
export const IconType = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4.4" y="4.4" width="15.2" height="15.2" rx="3.4" />
    <path d="M9 15.6 12 8.4l3 7.2M10.2 13h3.6" />
  </Svg>
);
export const IconGamepad = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6.8 8.2h10.4a3.6 3.6 0 0 1 3.5 2.8l.9 4.2a2.4 2.4 0 0 1-4.3 1.8l-1.4-1.7H8.1l-1.4 1.7a2.4 2.4 0 0 1-4.3-1.8l.9-4.2a3.6 3.6 0 0 1 3.5-2.8Z" />
    <path d="M8.3 11.4v2.8M6.9 12.8h2.8" />
    <circle cx="15.3" cy="12.4" r="1" />
    <circle cx="17.5" cy="14.3" r="1" />
  </Svg>
);

const TYPE_ICONS: Record<ItemType, (p: IconProps) => ReactElement> = {
  movie: IconCard,
  series: IconLayers,
  book: IconLibrary,
  game: IconGamepad,
  course: IconType,
  article: IconText,
  recipe: IconCard,
  video: IconCard,
  document: IconText,
  website: IconGlobe,
  other: IconLink,
};

export function TypeIcon({ type, size }: { type: ItemType; size?: number }) {
  const Component = TYPE_ICONS[type] ?? IconLink;
  return <Component size={size} />;
}

export const VISIBILITY_ICONS: Record<Visibility, (p: IconProps) => ReactElement> = {
  private: IconLock,
  followers: IconUsers,
  public: IconGlobe,
};
